import React, { CSSProperties, forwardRef, useState } from "react";

import { BoundingBoxesProvider } from "context/boundingBoxesContext";
import { ImageSettingsProvider } from "context/ImageSettingsContext/imageSettingsContext";
import { Canvas, CanvasRef } from "features/canvas/Canvas";
import { Point, Size } from "features/canvas/types";
import { CanvasAnnotation } from "types/imageAnnotations";

export interface ImageCanvasProps {
  imageUrl?: string;
  annotations?: CanvasAnnotation[];
  imageData?: [HTMLImageElement, "loaded" | "loading" | "failed"];
  style?: CSSProperties;
  /** The ID of the image query, used for generating the download filename */
  iqId?: string;
}

/**
 * A reusable component for displaying images with optional annotations (bounding boxes)
 * This component wraps the Canvas component with the necessary context providers and
 */
export const ImageCanvas = forwardRef<CanvasRef, ImageCanvasProps>(
  ({ imageUrl, annotations = [], imageData, style, iqId = "query" }, ref) => {
    const [boxContainerSize, setBoxContainerSize] = useState<Size>({
      width: 0,
      height: 0,
    });
    const [position, setPosition] = useState<Point>({
      x: 0,
      y: 0,
    });

    // if imageData or imageUrl is not provided in the props, throw an error
    if (!imageData && !imageUrl) {
      throw new Error("Either imageData or imageUrl must be provided");
    }

    return (
      <BoundingBoxesProvider isBoundingBoxes={annotations.length > 0}>
        <ImageSettingsProvider>
          <Canvas
            ref={ref}
            iqId={iqId}
            style={style}
            position={position}
            imageUrl={imageUrl}
            canInteract={false}
            imageData={imageData}
            setPosition={setPosition}
            annotations={annotations}
            boxContainerSize={boxContainerSize}
            setBoxContainerSize={setBoxContainerSize}
          />
        </ImageSettingsProvider>
      </BoundingBoxesProvider>
    );
  }
);
